import { useQuery } from '@tanstack/react-query';
import { publisherResultImportsApi } from '@/api/publisher-result-imports';
import { Danger } from '@/components/icons';
import { EmptyState } from '@/components/system';
import { Button } from '@/components/ui/button';
import { isPublisherResultImportActive } from '@/lib/publisher-result-imports';
import { formatImportDate, selectReportImportHistory } from './import-status';
import type { PublisherResultImport } from '@/types/publisher-result-import';

/** One page of the patient's imports; a single report never fills it. */
const PAGE_SIZE = 20;

type UseReportImportHistoryInput = {
    patientId: string | null | undefined;
    reportId: string | null | undefined;
    enabled: boolean;
};

export function useReportImportHistory({
    patientId,
    reportId,
    enabled,
}: UseReportImportHistoryInput) {
    const { data, isLoading } = useQuery({
        queryKey: ['publisher-result-imports', 'report-history', reportId],
        queryFn: async () => {
            const response = await publisherResultImportsApi.listPatientImports(patientId || '', {
                page: 1,
                pageSize: PAGE_SIZE,
            });

            return response.data;
        },
        enabled: enabled && Boolean(patientId) && Boolean(reportId),
    });

    return {
        records: selectReportImportHistory(data ?? [], reportId),
        isLoading,
    };
}

const getTableCount = (record: PublisherResultImport) =>
    record.extraction_json?.instruments.reduce(
        (sum, instrument) => sum + instrument.tables.length,
        0,
    ) ?? 0;

/** What the row still asks of the psychologist, if anything. */
const getRowAction = (record: PublisherResultImport) => {
    if (record.status === 'ready_for_approval')
        return { label: 'Revisar', action: 'open' } as const;
    if (record.status === 'duplicate_warning')
        return { label: 'Resolver', action: 'open' } as const;
    if (isPublisherResultImportActive(record))
        return { label: 'Acompanhar', action: 'open' } as const;
    if (record.status === 'failed') return { label: 'Enviar novamente', action: 'retry' } as const;

    return null;
};

const getRowDetail = (record: PublisherResultImport) => {
    const tableCount = getTableCount(record);
    if (record.status === 'approved' && tableCount > 0) {
        return `${tableCount} ${tableCount === 1 ? 'tabela aprovada' : 'tabelas aprovadas'}`;
    }

    if (tableCount > 0) {
        return `${tableCount} ${tableCount === 1 ? 'tabela extraída' : 'tabelas extraídas'}`;
    }

    return null;
};

export function ReportImportHistoryList({
    records,
    isLoading,
    onOpenImport,
    onRetry,
}: {
    records: PublisherResultImport[];
    isLoading: boolean;
    onOpenImport: (record: PublisherResultImport) => void;
    onRetry: () => void;
}) {
    if (isLoading) {
        return (
            <div className="rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground">
                Carregando importações...
            </div>
        );
    }

    if (records.length === 0) {
        return (
            <EmptyState
                title="Nenhuma importação neste laudo ainda"
                description="Envie um PDF na aba ao lado"
            />
        );
    }

    return (
        <ul className="overflow-hidden rounded-2xl border border-border">
            {records.map((record) => {
                const rowAction = getRowAction(record);
                const detail = getRowDetail(record);

                return (
                    <li
                        key={record.id}
                        className="flex items-center gap-3 border-t border-border bg-card px-3 py-2.5 first:border-t-0"
                    >
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-foreground">
                                {record.original_filename}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {formatImportDate(record.created_at)}
                                {detail ? ` · ${detail}` : ''}
                            </p>
                            {record.safe_error_message && (
                                <p className="mt-1 flex items-start gap-1.5 text-xs text-destructive-strong">
                                    <Danger className="mt-px size-3.5 shrink-0" />
                                    {record.safe_error_message}
                                </p>
                            )}
                        </div>
                        {rowAction && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="shrink-0"
                                onClick={() =>
                                    rowAction.action === 'retry' ? onRetry() : onOpenImport(record)
                                }
                            >
                                {rowAction.label}
                            </Button>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}
